// ── Session detail ("/sessions/:id") — the session workspace (Wave 6) ───────
// The full session record: an identity header (title + status + cohort) over the
// status sections (meeting/Zoom · recordings · transcript · invite-log · report ·
// metadata), each wrapping its OWN honest read, plus the role-gated WRITE flows
// (reschedule · lifecycle complete/cancel/archive · send-invites · reassign-coach).
//
// PARTICIPANT ISOLATION (blueprint §6 gate #1): the route manifest keeps
// /sessions/:id at allRoles, so a participant CAN reach this component — but the
// admin session workspace FAILS CLOSED for participant (and any non-workspace
// role): their own sessions are the Wave-9 dashboard, never here. The deny is
// checked BEFORE any read fires (enabled is also gated) so no session request is
// made for a denied role.
//
// GATED-DARK honesty: the meeting/recording/transcript/invite lanes render their
// dark state honestly (not-created / unavailable / pending), NEVER a fake Zoom
// meeting, join link, passcode, or transcript content. Transcript is server
// role-gated (participant + cohort_lead 403) and the transcript section also
// fail-closes for those roles. The id is a STRING — passed VERBATIM, never Number().

import { Alert, Badge, Button, DetailSurface, Module, ModuleBody, ModuleHead, useToast } from "@vjal/ui";
import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";

import { HonestState, useHonestQuery } from "../../components/honest-state";
import { destructiveButtonClass } from "../../components/ui-classes";
import { getCoachingServices } from "../../services/coaching-service-provider";
import { coachingQueryKeys } from "../../hooks/coaching-query-keys";
import { useSessionWorkflowMutations } from "../../hooks/useSessionWorkflowMutations";
import { evaluateActionCapability } from "../../domain/capabilities";
import { liveRecordIdFromPath } from "../product-live-rows";
import type {
  CoachingRecordInput,
  CoachingSessionDto,
  InviteOperationRequest,
  SessionLifecycleRequest,
  SessionRescheduleRequest
} from "../../services/coaching-dtos";
import type { ProductScreenContext } from "../product-route-contracts";
import {
  SESSION_LIFECYCLE_TRANSITIONS,
  type SessionLifecycleTransition,
  canAccessSessionsModule,
  isValidSessionId,
  orDash,
  sessionDisplayTitle,
  sessionStatusLabel,
  sessionStatusTone
} from "./sessions-helpers";
import { hasSessionStarted } from "./session-start";
import { SessionMeetingSection } from "./session-detail/session-meeting-section";
import { SessionRecordingsSection } from "./session-detail/session-recordings-section";
import { SessionTranscriptSection } from "./session-detail/session-transcript-section";
import { SessionInviteLogSection } from "./session-detail/session-invite-log-section";
import { SessionReportStatusSection } from "./session-detail/session-report-status-section";
import { SessionRescheduleModal } from "./session-reschedule-modal";
import { SessionLifecycleModal } from "./session-lifecycle-modal";
import { SessionInvitesModal } from "./session-invites-modal";
import { SessionReassignModal } from "./session-reassign-modal";
import { SessionPostSessionEmailSection } from "./session-detail/session-post-session-email-section";

const services = getCoachingServices();

export function SessionDetailPage({ role, routePath }: ProductScreenContext) {
  const toast = useToast();
  const mutations = useSessionWorkflowMutations();

  const allowReschedule = evaluateActionCapability(role, "sessions:reschedule").allowed;
  const allowLifecycle = evaluateActionCapability(role, "sessions:lifecycle").allowed;
  const allowInvites = evaluateActionCapability(role, "sessions:send-invites").allowed;
  const allowReassign = evaluateActionCapability(role, "sessions:reassign-coach").allowed;

  const sessionId = liveRecordIdFromPath(routePath, "sessions");
  const validId = isValidSessionId(sessionId);
  const workspaceDenied = !canAccessSessionsModule(role);

  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [invitesOpen, setInvitesOpen] = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [lifecycleTransition, setLifecycleTransition] = useState<SessionLifecycleTransition | null>(null);

  const query = useHonestQuery<CoachingSessionDto>(
    coachingQueryKeys.sessionDetail(sessionId ?? ""),
    () => services.sessions.detail(sessionId as string),
    { enabled: validId && !workspaceDenied }
  );
  const session = query.data ?? null;

  // ── Fail closed: non-workspace role -> permission-denied, NO read. ──────────
  if (workspaceDenied) {
    return (
      <section aria-label="Session detail" className="col-span-12" data-testid="session-detail-page">
        <div className="rounded-xl border border-line bg-white">
          <HonestState
            posture="permission-denied"
            title="You don't have access to this session"
            message="The session workspace is for coaching staff. Your own sessions are available on your dashboard."
          />
        </div>
      </section>
    );
  }

  // ── Bad/absent id, or a not-found / non-ready posture -> one honest body. ───
  if (!validId || query.posture !== null || !session) {
    const posture = !validId ? "empty" : (query.posture ?? "loading");
    const notFound = posture === "empty";
    return (
      <section aria-label="Session detail" className="col-span-12" data-testid="session-detail-page">
        <div className="rounded-xl border border-line bg-white">
          <HonestState
            posture={posture}
            gate={query.gate}
            reference={query.reference}
            onRetry={query.retry}
            title={notFound ? "Session not found" : undefined}
            message={
              notFound
                ? "This session doesn't exist, or it isn't in your workspace. It may have been removed."
                : undefined
            }
            canCreate={notFound}
            createAction={
              notFound ? (
                <Link to="/sessions" className="text-link no-underline hover:underline">
                  Back to sessions
                </Link>
              ) : undefined
            }
          />
        </div>
      </section>
    );
  }

  const terminal = session.status === "completed" || session.status === "cancelled" || session.status === "archived";
  // F16: a session whose start has passed is history; the API refuses to move it.
  const started = hasSessionStarted(session);
  const showActions = allowReschedule || allowLifecycle || allowInvites || allowReassign;

  // ── Write handlers (each toasts ONLY after the backend confirms) ────────────
  function runReschedule(body: SessionRescheduleRequest) {
    return mutations.rescheduleOne.mutateAsync({ sessionId: session!.id, body }).then((result) => {
      if (result.ok) {
        setRescheduleOpen(false);
        mutations.rescheduleOne.reset();
        toast.success("Session rescheduled");
      }
      return result;
    });
  }

  function runLifecycle(body: SessionLifecycleRequest) {
    return mutations.lifecycle.mutateAsync({ sessionId: session!.id, body }).then((result) => {
      if (result.ok) {
        setLifecycleTransition(null);
        mutations.lifecycle.reset();
        toast.success(`Session ${body.status}`);
      }
      return result;
    });
  }

  function runInvites(body: InviteOperationRequest) {
    return mutations.sendInvites.mutateAsync({ sessionId: session!.id, body }).then((result) => {
      if (result.ok) {
        setInvitesOpen(false);
        mutations.sendInvites.reset();
        // Honest: the 202 records the request as pending; never "delivered".
        toast.success("Invite request recorded (pending delivery)");
      }
      return result;
    });
  }

  function runReassign(body: CoachingRecordInput) {
    return mutations.reassignCoach.mutateAsync({ sessionId: session!.id, body }).then((result) => {
      if (result.ok) {
        setReassignOpen(false);
        mutations.reassignCoach.reset();
        toast.success("Coach reassigned");
      }
      return result;
    });
  }

  return (
    <section aria-label="Session detail" className="col-span-12 flex flex-col gap-4" data-testid="session-detail-page">
      {/* F3: real @vjal/ui DetailSurface for Detail-1 session-detail. */}
      <DetailSurface
        title={
          <span className="flex items-center gap-3">
            {sessionDisplayTitle(session.title, session.id)}
            <Badge variant={sessionStatusTone(session.status)}>{sessionStatusLabel(session.status)}</Badge>
          </span>
        }
        actions={
          showActions ? (
            <div className="flex flex-col items-start gap-2">
              <div className="flex flex-wrap gap-2">
                {allowInvites && (
                  <Button variant="secondary" onClick={() => setInvitesOpen(true)} aria-label="Send invites">
                    Send invites
                  </Button>
                )}
                {allowReschedule && (
                  <Button
                    variant="secondary"
                    onClick={() => setRescheduleOpen(true)}
                    disabled={terminal || started}
                    aria-label="Reschedule session"
                  >
                    Reschedule
                  </Button>
                )}
                {allowReassign && (
                  <Button variant="secondary" onClick={() => setReassignOpen(true)} aria-label="Reassign coach">
                    Reassign coach
                  </Button>
                )}
                {allowLifecycle &&
                  SESSION_LIFECYCLE_TRANSITIONS.map((transition) => (
                    <Button
                      key={transition.status}
                      variant={transition.destructive ? "danger" : "primary"}
                      className={transition.destructive ? destructiveButtonClass : undefined}
                      onClick={() => setLifecycleTransition(transition)}
                      disabled={terminal}
                      aria-label={transition.label}
                    >
                      {transition.label}
                    </Button>
                  ))}
              </div>
              {allowReschedule && started && !terminal ? (
                <Alert variant="warning">This session has already started, so it can't be rescheduled.</Alert>
              ) : null}
            </div>
          ) : undefined
        }
        fields={[
          {
            id: "back",
            label: "Directory",
            value: (
              <Link to="/sessions" className="text-link no-underline hover:underline">
                ← All sessions
              </Link>
            ),
          },
          {
            id: "cohort",
            label: "Cohort",
            value: orDash(session.cohort_name),
          },
          {
            id: "coach",
            label: "Coach",
            value: orDash(session.coach_name),
          },
          {
            id: "participants",
            label: "Participants",
            value: typeof session.participant_count === "number" ? session.participant_count : 0,
          },
        ]}
        titleExactName="Session title">
        {/* Status sections — each its OWN honest read; dark lanes render honestly. */}
        <div className="mt-4 grid grid-cols-12 gap-4">
          <SessionMeetingSection session={session} />
          <SessionReportStatusSection sessionId={session.id} />
          <SessionRecordingsSection sessionId={session.id} />
          <SessionTranscriptSection sessionId={session.id} role={role} />
          <SessionInviteLogSection sessionId={session.id} />
          <SessionPostSessionEmailSection sessionId={session.id} role={role} />
          <SessionMetadataSection session={session} />
        </div>
      </DetailSurface>

      {allowReschedule && (
        <SessionRescheduleModal
          open={rescheduleOpen}
          session={session}
          onClose={() => {
            setRescheduleOpen(false);
            mutations.rescheduleOne.reset();
          }}
          onConfirm={runReschedule}
          submitting={mutations.rescheduleOne.isPending}
          submitError={mutations.rescheduleOne.error}
        />
      )}
      {allowLifecycle && (
        <SessionLifecycleModal
          open={lifecycleTransition !== null}
          session={session}
          transition={lifecycleTransition}
          onClose={() => {
            setLifecycleTransition(null);
            mutations.lifecycle.reset();
          }}
          onConfirm={runLifecycle}
          submitting={mutations.lifecycle.isPending}
          submitError={mutations.lifecycle.error}
        />
      )}
      {allowInvites && (
        <SessionInvitesModal
          open={invitesOpen}
          session={session}
          onClose={() => {
            setInvitesOpen(false);
            mutations.sendInvites.reset();
          }}
          onConfirm={runInvites}
          submitting={mutations.sendInvites.isPending}
          submitError={mutations.sendInvites.error}
        />
      )}
      {allowReassign && (
        <SessionReassignModal
          open={reassignOpen}
          session={session}
          onClose={() => {
            setReassignOpen(false);
            mutations.reassignCoach.reset();
          }}
          onConfirm={runReassign}
          submitting={mutations.reassignCoach.isPending}
          submitError={mutations.reassignCoach.error}
        />
      )}
    </section>
  );
}

function MetadataField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-semibold uppercase tracking-wide text-ink-muted">{label}</span>
      <p className="m-0 text-sm text-ink-body">{children}</p>
    </div>
  );
}

/** Session metadata panel — read-only identity fields (no fabrication). */
function SessionMetadataSection({ session }: { session: CoachingSessionDto }) {
  return (
    <Module span={6}>
      <ModuleHead title="Session details" subtitle="Identity and scheduling fields." />
      <ModuleBody>
        <div className="grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
          <MetadataField label="Session id">{session.id}</MetadataField>
          <MetadataField label="Cohort">{orDash(session.cohort_name)}</MetadataField>
          <MetadataField label="Starts">{orDash(session.starts_at)}</MetadataField>
          <MetadataField label="Timezone">{orDash(session.timezone)}</MetadataField>
          <MetadataField label="Status">{sessionStatusLabel(session.status)}</MetadataField>
        </div>
      </ModuleBody>
    </Module>
  );
}
